import dayjs, { Dayjs } from "dayjs";
import { nanoid } from "nanoid";
import { useMemo } from "react";
import {
  EdmGetAllShiftsDocument,
  EdmGetAllVisitsForDeletedShiftsDocument,
  EdmMedicalAppShiftWithoutVisitFragment,
  EdmNonWorkingPeriod,
  useEdmGetAllDoctorsFromShiftsQuery,
  useEdmGetAllNonWorkingPeriodsQuery,
  useEdmGetAllShiftsQuery,
  useEdmGetAllStaffWithGenreAndWorkingHoursQuery,
  useEdmPublishShiftsMutation,
} from "lib/graphql/megaSchema";
import { TileColumn } from "lib/tools/createWorkspace/types";
import { useTileSystem } from "lib/tools/createWorkspace/useTileSystem";
import { toaster } from "lib/tools/toaster";
import {
  filterMedicalStaffOptionsByGenre,
  sortShifts,
} from "views/StaffSchedule/StaffScheduleCreator/tools";
import { StaffGenreFilter } from "views/StaffSchedule/tools";

type Props = {
  from: Dayjs;
  selectedStaffGenre: StaffGenreFilter;
  searchInput: string;
  selectedOrganizationId: string;
};

export const useScheduleCreator = ({
  from,
  selectedStaffGenre,
  selectedOrganizationId,
  searchInput,
}: Props) => {
  const formattedStartDate = from.format("YYYY-MM-DD");
  const formattedEndDate = from
    .endOf("month")
    .add(1, "day")
    .format("YYYY-MM-DD");

  const { addTile } = useTileSystem();

  const { data: allStaff, loading: allStafLoading } =
    useEdmGetAllStaffWithGenreAndWorkingHoursQuery({
      variables: {
        input: { genre: ["DOCTOR", "NURSE"], month: formattedStartDate },
      },
    });

  const {
    data: doctorsFromShiftsData,
    loading: doctorsFromShiftsLoading,
    refetch: getAllDoctorsFromShiftsQueryRefetch,
  } = useEdmGetAllDoctorsFromShiftsQuery({
    variables: {
      input: {
        date: formattedStartDate,
        organizationId: selectedOrganizationId,
      },
    },
    fetchPolicy: "network-only",
  });

  const [publishShifts, { loading: publishShiftsLoading }] =
    useEdmPublishShiftsMutation();

  const {
    data: shiftsFromSelectedOrganization,
    loading: shiftsFromSelectedOrganizationLoading,
  } = useEdmGetAllShiftsQuery({
    variables: {
      input: {
        from: formattedStartDate,
        to: formattedEndDate,
        organizationId: selectedOrganizationId,
      },
    },
    fetchPolicy: "network-only",
  });

  const {
    data: shiftsFromExcludedOrganization,
    loading: shiftsFromExcludedOrganizationLoading,
  } = useEdmGetAllShiftsQuery({
    variables: {
      input: {
        from: formattedStartDate,
        to: formattedEndDate,
        ignoredOrganizationId: selectedOrganizationId,
      },
    },
    fetchPolicy: "network-only",
  });

  const doctorsFromShiftsIdsArray =
    doctorsFromShiftsData?.edmGetAllDoctorsFromShiftsWithPreviousMonthResolver.map(
      (staff) => staff.id,
    );

  const { data: nonWorkingPeriodsData, loading: nonWorkingPeriodsLoading } =
    useEdmGetAllNonWorkingPeriodsQuery({
      variables: {
        input: {
          from: formattedStartDate,
          to: formattedEndDate,
          staffIds: doctorsFromShiftsIdsArray ?? [],
        },
      },
    });

  const medicalStaffSelectOptions = (
    allStaff?.edmGetAllStaffWithGenreAndWorkingHours ?? []
  ).map((o) => {
    const { medicalPerson, workedHours } = o;

    return {
      label: medicalPerson.name ?? "",
      value: {
        id: medicalPerson.id,
        genre: medicalPerson.genre,
        npwz: medicalPerson.npwz,
        avatarSrc: medicalPerson.avatarUrl,
        acceptChildren: medicalPerson.acceptChildren,
        workedHours,
      },
    };
  });

  const filteredMedicalStaff = useMemo(() => {
    const filteredMedicalStaffByOrganization = medicalStaffSelectOptions.filter(
      (staff) =>
        doctorsFromShiftsData?.edmGetAllDoctorsFromShiftsWithPreviousMonthResolver
          .map((doc) => doc.id)
          .includes(staff.value.id),
    );

    const filteredMedicalStaffByOrganizationAndGenre =
      filterMedicalStaffOptionsByGenre(
        filteredMedicalStaffByOrganization,
        selectedStaffGenre,
      );

    return filteredMedicalStaffByOrganizationAndGenre.filter((staff) =>
      staff.label
        .toLowerCase()
        .trim()
        .includes(searchInput.toLowerCase().trim()),
    );
  }, [
    medicalStaffSelectOptions,
    doctorsFromShiftsData,
    selectedStaffGenre,
    searchInput,
  ]);

  const allShifts: (
    | EdmMedicalAppShiftWithoutVisitFragment
    | EdmNonWorkingPeriod
  )[] = sortShifts([
    ...(shiftsFromSelectedOrganization?.edmGetAllShifts ?? []),
    ...(shiftsFromExcludedOrganization?.edmGetAllShifts ?? []),
    ...(nonWorkingPeriodsData?.edmGetAllNonWorkingPeriods ?? []),
  ]).filter((shift) => dayjs(shift.day).isSame(formattedStartDate, "month"));

  const handlePublishShifts = async () => {
    if (!selectedOrganizationId) {
      return;
    }

    const createPublishShiftsPromise = publishShifts({
      variables: {
        input: {
          fromDate: new Date(formattedStartDate),
          toDate: new Date(formattedEndDate),
          organizationId: selectedOrganizationId,
        },
      },
      refetchQueries: [
        EdmGetAllShiftsDocument,
        EdmGetAllVisitsForDeletedShiftsDocument,
      ],
    });

    await toaster.promise(createPublishShiftsPromise, {
      success: (data) => {
        getAllDoctorsFromShiftsQueryRefetch();

        if (data.data?.edmPublishShifts) {
          const scheduledVisitsWithDeletedShift = data.data.edmPublishShifts
            .filter(({ state }) => state === "DELETED")
            .flatMap(({ visits }) =>
              visits
                .filter(({ state }) => state === "SCHEDULED")
                .filter(({ scheduledOn }) =>
                  dayjs(scheduledOn).isAfter(dayjs()),
                ),
            )
            .sort((aShift, bShift) =>
              dayjs(aShift.scheduledOn).diff(dayjs(bShift.scheduledOn)),
            );

          if (scheduledVisitsWithDeletedShift.length > 0) {
            const id = nanoid();
            addTile(
              {
                name: "visits-with-deleted-shift-table",
                id,
                data: {
                  id,
                  visits: scheduledVisitsWithDeletedShift,
                  mode: "afterDeletedShift",
                },
              },
              TileColumn.Left,
            );
          }
        }

        return "Poprawnie opublikowano grafik";
      },
      loading: "Publikuję grafik...",
      error: "Nie udało się opublikować grafiku",
    });
  };

  return {
    filteredMedicalStaff,
    medicalStaffSelectOptions,
    shifts: {
      data: allShifts,
      loading:
        allStafLoading ||
        doctorsFromShiftsLoading ||
        shiftsFromSelectedOrganizationLoading ||
        shiftsFromExcludedOrganizationLoading ||
        nonWorkingPeriodsLoading,
    },
    publishShiftsLoading,
    handlePublishShifts,
  };
};
